import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Badge
} from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import dayjs from 'dayjs';
import 'dayjs/locale/it';

// Set Italian locale
dayjs.locale('it');

const Calendar = ({ courses, selectedDate, onDateSelect }) => {
  // Get course dates for highlighting
  const getCourseDates = () => {
    const dates = new Set();
    if (!courses || !Array.isArray(courses)) return dates;
    
    courses.forEach(course => {
      if (course.dates && Array.isArray(course.dates)) {
        course.dates.forEach(date => {
          if (date) {
            dates.add(dayjs(date).format('YYYY-MM-DD'));
          }
        });
      }
    });
    return dates;
  };

  const courseDates = getCourseDates();

  const getCoursesForDate = (date) => {
    if (!courses || !Array.isArray(courses)) return [];
    
    const dateStr = date.format('YYYY-MM-DD');
    return courses.filter(course => 
      course.dates && Array.isArray(course.dates) && course.dates.some(courseDate => 
        courseDate && dayjs(courseDate).format('YYYY-MM-DD') === dateStr
      )
    );
  };

  // Array of distinct colors for different courses
  const courseColors = [
    'tomato',
    'gold', 
    'lightblue',
    'limegreen',
    'violet',
    'orange',
    'pink',
    'turquoise',
    'coral',
    'lavender'
  ];

  // Function to get color for a course based on its ID
  const getCourseColor = (courseId) => {
    if (!courseId) return 'tomato';
    // Use the last character of the course ID to determine color
    const lastChar = courseId.toString().slice(-1);
    const colorIndex = parseInt(lastChar, 16) % courseColors.length;
    return courseColors[colorIndex];
  };

  // Custom day component to show course indicators
  const CustomDay = (props) => {
    const { day, outsideCurrentMonth, ...other } = props;
    const coursesForDay = getCoursesForDate(day);
    const hasCourses = courseDates.has(day.format('YYYY-MM-DD'));
    const isToday = dayjs().isSame(day, 'day');
    const isSelected = selectedDate && dayjs(selectedDate).isSame(day, 'day');

    // Get the color for the first course (or primary if multiple courses)
    const badgeColor = coursesForDay.length > 0 ? getCourseColor(coursesForDay[0]._id) : 'primary';

    return (
      <Box sx={{ position: 'relative' }}>
        <PickersDay
          {...other}
          day={day}
          outsideCurrentMonth={outsideCurrentMonth}
          sx={{
            backgroundColor: isToday ? 'primary.light' : 
                           isSelected ? 'secondary.light' : 
                           'transparent',
            border: isToday ? '2px solid primary.main' : 
                    isSelected ? '2px solid secondary.main' : 
                    '1px solid transparent',
            '&:hover': {
              backgroundColor: 'action.hover',
              transform: 'scale(1.02)',
              transition: 'all 0.2s'
            }
          }}
        />
        {hasCourses && (
          <Badge 
            badgeContent={coursesForDay.length} 
            sx={{
              position: 'absolute',
              top: +7,
              right: +7,
              '& .MuiBadge-badge': {
                fontSize: '0.6rem',
                height: '19px',
                backgroundColor: badgeColor,
                color: 'white',
                fontWeight: 'bold'
              }
            }}
          >
            {/* <Box sx={{ width: 0, height: 0 }} /> */}
          </Badge>
        )}
      </Box>
    );
  };

  return (
    <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, textAlign: 'center' }}>
        Calendario Corsi
      </Typography>
      
      <DateCalendar
        value={selectedDate}
        onChange={onDateSelect}
        slots={{ day: CustomDay }}
        slotProps={{
          day: {
            // Additional props for the custom day component
          }
        }}
        showDaysOutsideCurrentMonth={true}
        sx={{
          width: '100%',
          '& .MuiPickersCalendarHeader-root': {
            marginBottom: 1
          }
        }}
      />
    </Paper>
  );
};

export default Calendar; 